// Smart links — which addresses draw as a work item's card.
//
// Jira draws a pasted work-item link as a smart link (an ADF inlineCard):
// the work type's icon, the key, the summary and the status. Stride stores
// the same inlineCard node and draws it the same way. Two questions, kept
// apart on purpose:
//
//   pastedWorkItemUrl — does a paste BECOME a card? Only a bare address of
//     THIS site's own work item (https://<this site>/KEY or /browse/KEY).
//     Any other address stays a plain link, as it always has.
//     The client's OLD Jira site counts too (legacyJiraHosts, from the client
//     config seam): https://<old>.atlassian.net/browse/KEY pastes as a card
//     whose url is rewritten to this site's address of the same key.
//   cardKeyFromUrl    — does a stored card DRAW as a work item? This site's
//     address, or a Jira-era /browse/KEY address the clone carried over
//     (the item now lives here under the same key). The stored url is never
//     rewritten; when no visible item answers, the card draws as its address.
//   bareJiraIssueLink — a stored plain link whose text IS its Jira /browse/KEY
//     address (how Jira kept many pasted links) draws as a card as well;
//     a link with its own words stays a link.
//
// Pure: no React, no DOM beyond what a caller passes in.

import { issueKeyFromPath, workItemUrl } from './workItemLink.js';

function parseHttpUrl(text, origin) {
  let u;
  try { u = new URL(String(text || '').trim(), origin || undefined); } catch { return null; }
  return u.protocol === 'http:' || u.protocol === 'https:' ? u : null;
}

function hostOf(origin) {
  try { return new URL(origin).host; } catch { return null; }
}

// The key a stored card's url names, or null when it draws as a plain link.
export function cardKeyFromUrl(url, origin) {
  const u = parseHttpUrl(url, origin);
  if (!u) return null;
  const key = issueKeyFromPath(u.pathname);
  if (!key) return null;
  if (u.host === hostOf(origin)) return key;
  return /^\/browse\//.test(u.pathname) ? key : null;
}

// A host as the config names it: 'on-track.atlassian.net', with or without
// a scheme or a path, any case.
export function normaliseHost(h) {
  const t = String(h || '').trim().toLowerCase();
  if (!t) return null;
  try { return new URL(/^https?:\/\//.test(t) ? t : `https://${t}`).host || null; } catch { return null; }
}

// The url a paste should store as a card, or null and the paste goes on as
// before. A single absolute address of one of this site's work items is kept
// as pasted; one of the old Jira site's (/browse/KEY on a legacy host) becomes
// this site's address of that key.
export function pastedWorkItemUrl(text, origin, legacyJiraHosts = []) {
  const t = String(text || '').trim();
  if (!t || /\s/.test(t) || !/^https?:\/\//i.test(t)) return null;
  const u = parseHttpUrl(t);
  if (!u) return null;
  const key = issueKeyFromPath(u.pathname);
  if (!key) return null;
  if (u.host === hostOf(origin)) return t;
  const legacy = new Set((legacyJiraHosts || []).map(normaliseHost).filter(Boolean));
  if (legacy.has(u.host.toLowerCase()) && /^\/browse\//.test(u.pathname)) return workItemUrl(key, origin);
  return null;
}

// A stored text node that is nothing but a link to a Jira work item, its text
// the address itself → that address, else null. Only Atlassian Cloud hosts
// and only /browse/KEY, so ordinary links are never touched.
export function bareJiraIssueLink(node) {
  if (!node || node.type !== 'text' || !Array.isArray(node.marks) || node.marks.length !== 1) return null;
  const mark = node.marks[0];
  const href = mark && mark.type === 'link' ? String(mark.attrs?.href || '') : '';
  if (!href || String(node.text || '').trim() !== href) return null;
  const u = parseHttpUrl(href);
  if (!u || !/\.atlassian\.net$/i.test(u.host) || !/^\/browse\//.test(u.pathname)) return null;
  return issueKeyFromPath(u.pathname) ? href : null;
}

// The lozenge class for a status category colour (Jira's colorName).
export function statusClass(colorName) {
  if (colorName === 'green') return 'done';
  if (colorName === 'yellow') return 'in-progress';
  return 'todo';
}
